import { Stack, Typography } from '@mui/joy';
import { lowerFirst, upperFirst } from 'lodash';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { FileDropzone, SlidingPanelContent, useDocumentFilters } from 'core/components';
import { Loader } from 'core/elements';
import { AccessPermissions, FileExtension, FileUploadType, MimeType } from 'core/enums';
import { useFilesActions } from 'core/hooks';
import { QuoteEntityType } from 'quotes/enums';
import { useGetQuoteFiles, useQuoteActions } from 'quotes/hooks';
import { IBaseQuote, IQuoteFile } from 'quotes/types';
import { getQuoteVersionNumber } from 'quotes/utils';
import { Color, Direction, Level, Size } from 'theme/enums';

import { DocumentSection } from './DocumentSection';
import { QuoteFileSource } from './enums';
import { Filters } from './Filters';
import { IQuoteDocumentFilters } from './types';
import {
  createSourcesPredicate,
  createUploadDownloadPredicate,
  isBomImportSectionVisible,
  isSpaSentSectionVisible,
} from './utils';

interface IProps {
  quote: IBaseQuote;
  entityType: QuoteEntityType;
}

export const QuoteDocuments = ({ quote, entityType }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { fileUpload } = useFilesActions();

  const { attachQuoteFile, detachQuoteFile } = useQuoteActions({ entityType });
  const { records, isLoading } = useGetQuoteFiles({ filters: { quoteId: quote.id } });

  const {
    bomImport = [],
    vendorQuoteImport = [],
    quoteDownload = [],
    spaSent = [],
    upload = [],
  } = records ?? {};

  const isBomImportVisible = useMemo(
    () => isBomImportSectionVisible({ quote, entityType, bomImportCount: bomImport.length }),
    [bomImport.length, entityType, quote]
  );

  const isSpaSentVisible = useMemo(
    () =>
      isSpaSentSectionVisible({
        quote,
        user,
        hasSendAsSpaRight: hasPermission(AccessPermissions.SEND_AS_SPA),
        spaSentCount: spaSent.length,
      }),
    [hasPermission, quote, spaSent.length, user]
  );

  const allRecords = useMemo<IQuoteFile[]>(() => Object.values(records ?? {}).flat(), [records]);

  const fileSourceOptions = useMemo(() => {
    return Array.from(new Set(allRecords?.map((record) => record.source)));
  }, [allRecords]);

  const {
    filters,
    setFilters,
    getActiveFilterCount,
    clearFilters,
    filteredRecords,
    fileTypeOptions,
  } = useDocumentFilters<IQuoteDocumentFilters, IQuoteFile>({
    records: allRecords,
    customPredicates: [createUploadDownloadPredicate, createSourcesPredicate],
  });

  const acceptFileTypes = {
    [MimeType.XLS]: [FileExtension.XLS],
    [MimeType.XLSX]: [FileExtension.XLSX],
    [MimeType.CSV]: [FileExtension.CSV],
    [MimeType.PDF]: [FileExtension.PDF],
    [MimeType.JPEG]: [FileExtension.JPEG],
    [MimeType.PNG]: [FileExtension.PNG],
  };

  const onUpload = async (files: File[]) => {
    const { data } = await fileUpload(files, {
      fileType: FileUploadType.QUOTE_ATTACHMENT,
    });

    const fileId = data[0].id;

    await attachQuoteFile({
      quoteFileAssociation: {
        quoteId: quote.id,
        fileId,
        source: QuoteFileSource.UPLOAD,
      },
    });
  };

  const onRemoveFile = async (fileId: number) =>
    await detachQuoteFile({
      ids: [fileId],
    });

  const filteredIds = useMemo(
    () => new Set(filteredRecords.map((file) => file.id)),
    [filteredRecords]
  );

  const quoteDocumentsTitle = (
    <Trans
      i18nKey="labelQuoteDocuments"
      values={{
        quoteTitle: t('titleQuote', {
          id: quote?.id,
          version: getQuoteVersionNumber(quote),
          entityType,
          quoteType: upperFirst(quote.type),
        }),
        entityType: lowerFirst(entityType),
      }}
      components={{ quoteTitleWrapper: <Typography color={Color.PRIMARY} /> }}
    />
  );

  return (
    <SlidingPanelContent
      title={t('titleDocuments')}
      // fullWidth defaults to a fixed 1440px wrapper that overflows the narrower
      // (1272px) details tab panel, causing a horizontal scroll; false makes the
      // wrapper flex to fit the tab. Same fix as AttributesCard.
      fullWidth={false}
    >
      <FileDropzone accept={acceptFileTypes} onUpload={onUpload} style={{ height: '113px' }} />

      <Stack gap={2}>
        <Filters
          filters={filters}
          setFilters={setFilters}
          activeFilterCount={getActiveFilterCount()}
          clearFilters={clearFilters}
          fileSourceOptions={fileSourceOptions}
          fileTypeOptions={fileTypeOptions}
          entityType={entityType}
          quoteType={quote.type}
        />

        <Stack direction={Direction.ROW} alignItems="center" gap={2}>
          <Typography level={Level.TITLE_LG}>{quoteDocumentsTitle}</Typography>
          {isLoading && (
            <Loader size={Size.SM} style={{ justifyContent: 'flex-start', width: 'fit-content' }} />
          )}
        </Stack>

        {!isLoading && (
          <Stack
            sx={{
              gap: 2,
              minWidth: 0,
              maxHeight: 'calc(100vh - 495px)',
              overflowY: 'auto',
            }}
          >
            {isSpaSentVisible && (
              <DocumentSection
                source={QuoteFileSource.SPA_SENT}
                records={spaSent}
                quote={quote}
                entityType={entityType}
                filteredIds={filteredIds}
                onRemoveFile={onRemoveFile}
              />
            )}

            <DocumentSection
              source={QuoteFileSource.UPLOAD}
              records={upload}
              quote={quote}
              entityType={entityType}
              filteredIds={filteredIds}
              onRemoveFile={onRemoveFile}
            />

            {isBomImportVisible && (
              <DocumentSection
                source={QuoteFileSource.BOM_IMPORT}
                records={bomImport}
                quote={quote}
                entityType={entityType}
                filteredIds={filteredIds}
                onRemoveFile={onRemoveFile}
              />
            )}

            <DocumentSection
              source={QuoteFileSource.VENDOR_QUOTE_IMPORT}
              records={vendorQuoteImport}
              quote={quote}
              entityType={entityType}
              filteredIds={filteredIds}
              onRemoveFile={onRemoveFile}
            />

            <DocumentSection
              source={QuoteFileSource.DOWNLOAD}
              records={quoteDownload}
              quote={quote}
              entityType={entityType}
              filteredIds={filteredIds}
              onRemoveFile={onRemoveFile}
            />
          </Stack>
        )}
      </Stack>
    </SlidingPanelContent>
  );
};
